import { ChangeDetectionStrategy, Component, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AuthenticationService, CoursesService, PathsService } from '@draeger-academy/common';
import { ConfigurationService } from '@draeger-academy/common/lib/services/configuration/configuration.service';
import { CartItem } from 'models/cartItem';
import { MyCourseItem } from 'models/myCourse';
import { BehaviorSubject, Observable } from 'rxjs';
import { GenericModalService } from '../generic-modal';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'da-my-course-item',
    templateUrl: './my-course-item.component.html',
    styleUrls: ['./my-course-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MyCourseItemComponent implements OnInit {
    @Input() course: MyCourseItem;
    @Input() status: any;
    @Input() testResults: any;
    @Input() mycourses: MyCourseItem[];
    public dynamicParameter: string;
    public language: string;
    public courseDetail: string;
    public booking: string;
    public courseImage: string;
    public alt = '';
    public currency = '';
    public bookingDetails = '';
    public countryName = '';
    public translationPrefix = 'myCourseItem';
    private _modalId: string;
    public currentStatus: string;
    public testResult = '';
    public loggedIn: boolean;
    private _$subModulesAvailable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    @ViewChild('overlay', { static: true }) private _overlay;
    @ViewChild('modal', { static: true }) private _modal;
    private _$course: BehaviorSubject<MyCourseItem> = new BehaviorSubject<MyCourseItem>(null);

    private _$location: BehaviorSubject<MyCourseItem> = new BehaviorSubject<MyCourseItem>(null);

    constructor(
        private _genericModalService: GenericModalService,
        public config: ConfigurationService,
        private _pathsService: PathsService,
        private authenticationService: AuthenticationService,
        private _courseService: CoursesService,
        private _translateService: TranslateService
    ) {}

    public get course$(): Observable<MyCourseItem> {
        return this._$course.asObservable();
    }

    public get subModulesAvailable$(): Observable<boolean> {
        return this._$subModulesAvailable.asObservable();
    }

    public get location$(): Observable<MyCourseItem> {
        return this._$location.asObservable();
    }

    private _$testResult: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public get testResult$(): Observable<string> {
        return this._$testResult.asObservable();
    }

    private _$dateFormat: BehaviorSubject<string> = new BehaviorSubject('');
    public get dateFormat$(): Observable<string> {
        return this._$dateFormat.asObservable();
    }

    private _$downloadLabel: BehaviorSubject<string> = new BehaviorSubject('');
    public get downloadLabel$(): Observable<string> {
        return this._$downloadLabel.asObservable();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['status']) {
            if (this.status && this.status.length > 0) {
                this.status.forEach((element) => {
                    if (element.id === this.course.status) {
                        if (element.id !== 'WAITINGLIST') {
                            this.currentStatus = element.name.toLowerCase();
                        } else {
                            this.currentStatus = element.name;
                        }
                    }
                });
            }
        }
        if (changes['testResults']) {
            if (this.testResults && this.testResults.length > 0) {
                this.testResults.forEach((element) => {
                    if (element.id === this.course.testResult) {
                        this.testResult = element.name.toLowerCase();
                        this._$testResult.next(this.testResult);
                    }
                });
            }
        }
        if (changes['course']) {
            this._findTranslationPrefix();
            if (this.course.certificateUrl) {
                this._translateService.get(this.translationPrefix + '.downloadButtonLabel').subscribe((data) => {
                    this._$downloadLabel.next(data);
                });
            }
        }
    }

    ngOnInit() {
        this.getUserCourses();
        if (this.authenticationService.isLoggedIn()) {
            this.loggedIn = true;
        } else {
            this.loggedIn = false;
        }
        this.courseDetail = this._pathsService.getAccountCourseDetailLink(this.course.courseId);
        this.bookingDetails = this._pathsService.getAccountBookingDetailsLink(this.course.id);

        if (this.course) {
            this._$course.next(this.course);
        }
        this._findTranslationPrefix();

        if (this.status && this.status.length > 0) {
            this.status.forEach((element) => {
                if (element.id === this.course.status) {
                    if (element.id !== 'WAITINGLIST') {
                        this.currentStatus = element.name.toLowerCase();
                    } else {
                        this.currentStatus = element.name;
                    }
                }
            });
        }

        this._courseService.updatedCourses$.subscribe((courses) => {
            if (courses) {
                const course = courses.find((x) => x.id === this.course.id);
                if (course) {
                    this._$course.next(course);
                    this.course = course;
                    this._updateTestResult();
                }
            }
        });

        this._updateTestResult();

        this.language = this.config.getCurrentSite();
        const isPortal = this.config.isPartnerPage();
        if (isPortal) {
            this._$dateFormat.next(this.config.$portalPreferences.getValue().dateFormat);
        } else {
            this._$dateFormat.next('dd.MM.yyyy');
        }
    }

    private _updateTestResult() {
        if (this.course.testResult && this.course.testResult.length > 0) {
            if (this.testResults && this.testResults.length > 0) {
                this.testResults.forEach((element) => {
                    if (element.id === this.course.testResult) {
                        this._$testResult.next(element.name);
                    }
                });
            }
        } else {
            if (this.course.isProduct === true) {
                if (
                    this.course.deliveryDate &&
                    this.course.deliveryDate !== null &&
                    this.course.deliveryDate !== '' &&
                    this.course.deliveryDate !== '0001-01-01T00:00:00'
                ) {
                    this.testResults.forEach((element) => {
                        if (element.id === 'INCOMPLETE') {
                            this._$testResult.next(element.name);
                        }
                    });
                } else {
                    if (
                        this.course.deliveryDate === null ||
                        !this.course.deliveryDate ||
                        this.course.deliveryDate === '' ||
                        this.course.deliveryDate === '0001-01-01T00:00:00'
                    ) {
                        this.testResults.forEach((element) => {
                            if (element.id === 'NOT ATTEMPTED') {
                                this._$testResult.next(element.name);
                            }
                        });
                    }
                }
            }
        }
    }

    public editBooking(): void {
        this._overlayHandler(false);
    }

    public openOverlay(event: CartItem | MyCourseItem): void {
        this.config.$siteSettings.subscribe((data) => {
            if (data && data.length > 0) {
                const _countriesData = data.filter((f) => f.key.toLowerCase() === 'countries')[0];
                _countriesData['value'].filter((obj) => {
                    if (obj.code === event.location.countryKey) {
                        this.countryName = obj.name;
                    }
                });
                this._$location.next(this.course);
                this._overlayHandler(true);
            }
        });
    }

    private _overlayHandler(modal): void {
        if (modal) {
            this._modalId = this._genericModalService.create(this._overlay);
            this._genericModalService.open(this._modalId, this._overlay, true);
        } else {
            this._modalId = this._genericModalService.create(this._modal);
            this._genericModalService.open(this._modalId, this._modal, true);
        }
    }

    public closeModal() {
        this._genericModalService.close(this._modalId);
    }

    private getUserCourses() {
        if (this.course.infoPackageAvailable && this.course.infoPackageSubModuleIds.length > 0) {
            const arr = [];
            for (const i of this.mycourses) {
                if (this.course.infoPackageSubModuleIds.indexOf(i.id) !== -1) {
                    arr.push(i);
                }
            }
            if (arr.length > 0) {
                this._$subModulesAvailable.next(true);
            } else {
                this._$subModulesAvailable.next(false);
            }
        }
    }

    public storeID() {
        sessionStorage.setItem(`myCourseID`, this.course.courseId);
    }

    private _findTranslationPrefix() {
        if (this.config.isPartnerPage()) {
            this.translationPrefix = 'account.courseItem';
        }
    }
}
